import { classNames } from '@shopify/css-utilities'
import { Link, Image } from '@shopify/hydrogen'
import { Image as ImageType } from '@shopify/hydrogen/storefront-api-types'
import { CSSProperties, useContext, useEffect, useRef, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import styles from '../../assets/scss/components/home/home.module.scss'
import { AcfImage } from '../../lib/acf'
import { EnhancedMenu } from '../../lib/menus'
import { ProductMetafields } from '../../lib/types'
import { HeaderContext } from '../header/header-provider.client'
import { useI18n } from '../i18n.client'
import { BreakpointsContext } from '../layout/breakpoints-provider.client'
import { NewsletterForm } from '../newsletter-form.client'
import { AssetLoader, AssetLoaderImage, StaticAssetLoader } from './asset-loader.client'
import { Footer } from './footer.client'
import { useHome } from '../../lib/hooks/home.client'

const inertia = 80

interface Menus {
  companyMenu: EnhancedMenu
  helpMenu: EnhancedMenu
  legalMenu: EnhancedMenu
  shopMenu: EnhancedMenu
}

interface ProductCarouselType {
  images?: AcfImage[][]
  products: ProductMetafields[]
  rollovers?: AcfImage[][]
}

interface ProductFeatureType {
  badge?: ImageType
  cta?: string
  description?: string
  image?: ImageType
  product?: ProductMetafields
  rollover?: ImageType
  url?: string
}

export const Home: React.FC<{
  bottomLeftFeature: ProductFeatureType
  bottomRightFeature: ProductFeatureType
  campaignHero: ProductFeatureType
  carousel: ProductCarouselType
  leftFeature: ProductFeatureType
  lookbook: ProductFeatureType
  menus: Menus
  newsletter: ProductFeatureType
  rightFeature: ProductFeatureType
  topLeftFeature: ProductFeatureType
  topRightFeature: ProductFeatureType
}> = ({
  bottomLeftFeature,
  bottomRightFeature,
  campaignHero,
  carousel,
  leftFeature,
  lookbook,
  menus,
  newsletter,
  rightFeature,
  topLeftFeature,
  topRightFeature,
}) => {
  const { isLargeUp } = useContext(BreakpointsContext)
  const { isCartDrawerOpen, isMainNavigationOpen } = useContext(HeaderContext)
  const { canvasHeight, canvasWidth } = useHome()
  const [clientWidth, setClientWidth] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)
  const [initialOffsetX, setInitialOffsetX] = useState(0)
  const [initialOffsetY, setInitialOffsetY] = useState(0)
  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)
  const [delayedX, setDelayedX] = useState(0)
  const [delayedY, setDelayedY] = useState(0)

  const setOffsets = ({ x, y }: { x: number; y: number }) => {
    const centerX = document.body.clientWidth / 2
    const centerY = document.body.clientHeight / 2
    const widthMultiplier = (canvasWidth - document.body.clientWidth) / document.body.clientWidth
    const heightMultiplier = (canvasHeight - document.body.clientHeight) / document.body.clientHeight

    setOffsetX((centerX - x) * widthMultiplier)
    setOffsetY((centerY - y) * heightMultiplier)
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (!('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      setOffsets({ x: event.clientX, y: event.clientY })
    }
  }

  const handleResize = () => {
    setClientWidth(document.body.clientWidth)
    setClientHeight(document.body.clientHeight)
    setInitialOffsetX((document.body.clientWidth - canvasWidth) / 2)
    setInitialOffsetY((document.body.clientHeight - canvasHeight) / 2)
  }

  useEffect(() => {
    handleResize()

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', handleResize)
    }
  }, [canvasHeight, canvasWidth])

  useInterval(() => {
    if (isCartDrawerOpen || isMainNavigationOpen) {
      return
    }

    const distanceX = Math.abs(offsetX - delayedX)
    const distanceY = Math.abs(offsetY - delayedY)

    if (distanceX < 1) {
      setDelayedX(offsetX)
    } else {
      let speed = Math.round(distanceX / inertia)

      if (speed < 1) {
        speed = 1
      }

      setDelayedX(delayedX < offsetX ? delayedX + speed : delayedX - speed)
    }

    if (distanceY < 1) {
      setDelayedY(offsetY)
    } else {
      let speed = Math.round(distanceY / inertia)

      if (speed < 1) {
        speed = 1
      }

      setDelayedY(delayedY < offsetY ? delayedY + speed : delayedY - speed)
    }
  }, 10)

  const finalOffsetX = clientWidth < canvasWidth ? initialOffsetX + delayedX : 0
  const finalOffsetY = clientHeight < canvasHeight ? initialOffsetY + delayedY : 0

  return (
    <section className="section">
      <div className={styles.container}>
        <div
          className={styles.canvas}
          style={
            isLargeUp
              ? {
                  height: canvasHeight,
                  transform: `translate(${finalOffsetX}px, ${finalOffsetY}px)`,
                  width: canvasWidth,
                }
              : {}
          }>
          <div className={styles.campaignHeroContainer}>
            <ProductFeatureRollover {...campaignHero} className={styles.campaignHero} hasRolloverOnMobile />
            <StaticAssetLoader
              alt="Handmade jewelry"
              className={styles.handmadeJewelery}
              height={182}
              src="/handmade-jewelry.svg"
              style={{ zIndex: 1 }}
              width={303}
            />
            <StaticAssetLoader
              className={styles.shootingStar}
              height={146}
              src="/shooting-star.png"
              style={{ zIndex: 1 }}
              width={165}
            />
          </div>
          <div className={styles.leftFeatureContainer}>
            <ProductFeature {...leftFeature} className={styles.leftFeature} isTitleVisible />
            <StaticAssetLoader
              className={styles.marieLichtenbergBadge}
              height={148}
              src="/marie-lichtenberg-badge.png"
              width={162}
            />
            <StaticAssetLoader className={styles.leftNote} height={322} src="/left-note.png" width={323} />
            <StaticAssetLoader
              className={styles.rainbowBadge}
              height={51}
              src={leftFeature.badge?.url || '/rainbow-badge.png'}
              width={135}
            />
          </div>
          <div className={styles.raizinFeatureContainer}>
            <a className={styles.raizinCard} href="https://raiz-in.com">
              <StaticAssetLoader height={306} src="/raizin-card.png" width={305} />
            </a>
          </div>
          <div className={styles.rightFeatureContainer}>
            <ProductFeature {...rightFeature} className={styles.rightFeature} isTitleVisible />
            <StaticAssetLoader className={styles.eye} height={40} src="/eye.png" width={51} />
            <StaticAssetLoader className={styles.star1} height={29} src="/star-1.png" width={26} />
            <StaticAssetLoader className={styles.ball} height={108} src="/ball.gif" width={124} />
          </div>
          <div className={styles.lookbookContainer}>
            <ProductFeatureRollover {...lookbook} className={styles.lookbook} style={{ zIndex: 1 }} />
          </div>
          <div className={styles.topRightFeatureContainer}>
            <StaticAssetLoader
              className={styles.topRightFeature}
              height={523}
              src={topRightFeature.image?.url || '/top-right-feature.png'}
              width={418}
            />
            <StaticAssetLoader className={styles.topRightNote} height={306} src="/top-note.png" width={305} />
            <StaticAssetLoader
              className={styles.rainbowEyeBadge}
              height={82}
              src={topRightFeature.badge?.url || '/rainbow-eye-badge.png'}
              width={147}
            />
            <StaticAssetLoader className={styles.star2} height={21} src="/star-2.png" width={23} />
          </div>
          <div className={styles.carouselOuterContainer}>
            <ProductCarousel {...carousel} />
            <StaticAssetLoader className={styles.star3} height={30} src="/star-3.png" width={27} />
            <StaticAssetLoader className={styles.star4} height={28} src="/star-4.png" width={26} />
          </div>
          <div className={styles.topLeftFeatureContainer}>
            <ProductFeatureRollover {...topLeftFeature} className={styles.topLeftFeature} />
          </div>
          <div className={styles.newsletterContainer}>
            <Newsletter newsletter={newsletter} />
          </div>
          <div className={styles.bottomLeftFeatureContainer}>
            <ProductFeatureRollover
              {...bottomLeftFeature}
              className={styles.bottomLeftFeature}
              hasRolloverOnMobile
              style={{ zIndex: 1 }}
            />
            <StaticAssetLoader
              className={styles.bottomLeftFeatureBackground}
              height={319}
              src="/bottom-left-feature.png"
              width={298}
            />
          </div>
          <div className={styles.bottomRightFeatureContainer}>
            <ProductFeature {...bottomRightFeature} className={styles.bottomRightFeature} />
          </div>
          <a href="https://www.instagram.com/marie.lichtenberg/" target="_blank" rel="nofollow">
            <StaticAssetLoader className={styles.instagram} height={80} src="/instagram.svg" width={94} />
          </a>
          <StaticAssets />
          <Footer {...menus} />
        </div>
      </div>
    </section>
  )
}

const Newsletter: React.FC<{ newsletter: ProductFeatureType }> = ({ newsletter }) => {
  const [i18n] = useI18n()
  const { isLargeUp, isXLargeUp } = useContext(BreakpointsContext)
  const ref = useRef<HTMLAnchorElement>(null)

  let height = 375
  if (isXLargeUp) {
    height = 350
  } else if (isLargeUp) {
    height = 260
  }

  let width = 256
  if (isXLargeUp) {
    width = 513
  } else if (isLargeUp) {
    width = 381
  }

  return (
    <AssetLoader additionalImages={['/notebook-closed.png']} rect={ref.current?.getBoundingClientRect()}>
      <a className={classNames(styles.newsletter, styles.hasRollover, styles.hasRolloverOnMobile)} ref={ref}>
        <AssetLoaderImage
          alt=""
          height={height}
          src={isLargeUp ? '/notebook-open-large.png' : '/notebook-open-small.png'}
          width={width}
        />
        <div className={styles.form}>
          <p>{newsletter.description}</p>
          <NewsletterForm variant="newsletter_subscription_page" />
        </div>
        <div className={styles.title}>{i18n.translate('sign-up')}</div>
      </a>
    </AssetLoader>
  )
}

const ProductFeatureRollover: React.FC<{
  className: string
  description?: string
  image?: ImageType
  rollover?: ImageType
  hasRolloverOnMobile?: boolean
  product?: ProductMetafields
  style?: Pick<CSSProperties, 'zIndex'>
}> = ({ className, description, hasRolloverOnMobile, image, product, rollover, style }) => {
  const ref = useRef<HTMLAnchorElement>(null)

  if (!image || !rollover) {
    return null
  }

  if (product) {
    return (
      <AssetLoader additionalImages={[image.url]} rect={ref.current?.getBoundingClientRect()} style={style}>
        <Link
          className={classNames(
            'underline',
            className,
            styles.hasRollover,
            hasRolloverOnMobile && styles.hasRolloverOnMobile,
          )}
          ref={ref}
          to={`/products/${product.handle}`}>
          <div className={styles.backgroundImage} style={{ backgroundImage: `url('${image.url}')` }}>
            <AssetLoaderImage
              alt={product.title}
              height={rollover.height ?? 'auto'}
              src={rollover.url}
              width={rollover.width ?? 'auto'}
            />
          </div>
          <div className={styles.title}>
            <span className="target">{product.title}</span>
          </div>
        </Link>
      </AssetLoader>
    )
  }

  return (
    <AssetLoader additionalImages={[image.url]} rect={ref.current?.getBoundingClientRect()} style={style}>
      <a
        className={classNames(className, styles.hasRollover, hasRolloverOnMobile && styles.hasRolloverOnMobile)}
        ref={ref}
        style={{ backgroundImage: `url('${image.url}')` }}>
        <AssetLoaderImage
          alt=""
          height={rollover.height ?? 'auto'}
          src={rollover.url}
          width={rollover.width ?? 'auto'}
        />
        <div className={styles.title}>{description}</div>
      </a>
    </AssetLoader>
  )
}

const ProductFeature: React.FC<{
  className: string
  cta?: string
  description?: string
  image?: ImageType
  isTitleVisible?: boolean
  product?: ProductMetafields
  url?: string
}> = ({ className, cta, description, image, isTitleVisible = false, product, url }) => {
  const ref = useRef<HTMLAnchorElement>(null)

  if (!image) {
    return null
  }

  if (product) {
    return (
      <AssetLoader additionalImages={[image.url]} rect={ref.current?.getBoundingClientRect()}>
        <Link className={classNames('underline', className)} ref={ref} to={url || `/products/${product.handle}`}>
          <AssetLoaderImage
            alt={product.title}
            height={image.height ?? 'auto'}
            src={image.url}
            width={image.width ?? 'auto'}
          />
          {isTitleVisible && (
            <div className={styles.title}>
              <span className="target">{cta || product.title}</span>
            </div>
          )}
        </Link>
      </AssetLoader>
    )
  }

  return (
    <AssetLoader additionalImages={[image.url]} rect={ref.current?.getBoundingClientRect()}>
      <a className={className} ref={ref}>
        <AssetLoaderImage alt={''} height={image.height ?? 'auto'} src={image.url} width={image.width ?? 'auto'} />
        {isTitleVisible && <div className={styles.title}>{description}</div>}
      </a>
    </AssetLoader>
  )
}

const ProductCarousel: React.FC<{
  images?: AcfImage[][]
  products: ProductMetafields[]
  rollovers?: AcfImage[][]
}> = ({ images, products, rollovers }) => {
  const [i18n] = useI18n()
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLAnchorElement>(null)

  if (!images || !rollovers) {
    return null
  }

  const decrementActiveIndex = () => {
    setActiveIndex(activeIndex - 1 < 0 ? products.length - 1 : activeIndex - 1)
  }

  const incrementActiveIndex = () => {
    setActiveIndex(activeIndex + 1 > products.length - 1 ? 0 : activeIndex + 1)
  }

  const additionalImages = images.map(([image]) => image.src)
  const rolloverImages = rollovers.map(([image]) => image.src)

  return (
    <AssetLoader
      additionalImages={[...additionalImages, ...rolloverImages]}
      defaultIsLoaded
      rect={ref.current?.getBoundingClientRect()}>
      <div className={styles.carouselInnerContainer}>
        <div className={styles.carousel}>
          <Image
            alt={i18n.translate('previous')}
            className={styles.carouselPrevious}
            height={8}
            onClick={decrementActiveIndex}
            src="/carousel-arrow.svg"
            width={27}
          />
          {images.map(([image], index) => (
            <Link
              className={classNames(styles.carouselItem, styles.hasRollover, activeIndex === index && styles.active)}
              key={image.id}
              ref={ref}
              style={{ backgroundImage: `url('${image.src}')` }}
              to={`/products/${products[activeIndex].handle}`}>
              <img
                alt=""
                decoding="async"
                height={rollovers[index][0].height ?? 'auto'}
                loading="lazy"
                src={rollovers[index][0].src}
                width={rollovers[index][0].width ?? 'auto'}
              />
            </Link>
          ))}
          <Image
            alt={i18n.translate('next')}
            className={styles.carouselNext}
            height={8}
            onClick={incrementActiveIndex}
            src="/carousel-arrow.svg"
            width={27}
          />
        </div>
        <Link className={styles.title} to={`/products/${products[activeIndex].handle}`}>
          {products[activeIndex].title}
        </Link>
      </div>
    </AssetLoader>
  )
}

const StaticAssets: React.FC = () => {
  return (
    <>
      <StaticAssetLoader
        alt="Jewelry made with hand"
        className={styles.jewelryMadeWithHand}
        height={112}
        src="/jewelry-made-with-hand.svg"
        width={377}
      />
      <StaticAssetLoader
        alt="Speed dating"
        className={styles.speedDating}
        height={80}
        src="/speed-dating.svg"
        width={209}
      />
      <StaticAssetLoader
        alt="Paris, France"
        className={styles.parisFrance}
        height={209}
        src="/paris-france.svg"
        width={119}
      />
      {/* <StaticAssetLoader
        alt="Heart and soul made"
        className={styles.heartAndSoulMade}
        height={124}
        src="/heart-and-soul-made.svg"
        style={{ zIndex: 1 }}
        width={337}
      /> */}
    </>
  )
}
